import React, { useMemo, useRef, useState } from 'react';
import Intl from '@fx-ui/intl';
import { Button } from '@fx-ui/fine-design';
import {
    assign,
    compact,
    get,
    getApi,
    getChinaCode,
    getEncryptedPassword,
    getErrorMsg,
    getUrlParameter,
    isEmpty,
    isMobileUA,
    post,
    setTrackerConfig,
    size,
    tracker
} from '~/utils';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { InvitationInfo, SuiteId } from '~/interfaces';
import { API, ECode, TRACKER, URL_PARAMETER } from '~/constants';
import AccountForm, {
    AccountFieldType,
    AccountFormRef,
    AccountFormValue
} from '~/mixins/account/containers/account-form';
import {
    defaultEmail,
    defaultNickname,
    defaultPhone,
    getAuthParamsOnRegister,
    isAccountInvitation,
    isInvitationMode,
    isPublicInvitation
} from '../../utils/invite';
import { handleRedirect } from '../../utils/register';
import useJoinCorp from '../../hooks/useJoinCorp';
import { getBrandInfo } from '~/utils/brand';
import classNames from 'classnames';

export enum RegisterType {
    phone = 'phone',
    email = 'email'
}

interface RegisterFormProps {
    /**
     * 注册方式
     */
    items?: RegisterType[];
    skipAppInstall?: boolean;
    suiteId?: SuiteId;
    onRegister?: (data: { user?: Record<string, string> }) => void;
    onComplete?: (data: { invitations: InvitationInfo[] }) => void;
}

const DEFAULT_REGISTER_ITEMS = [RegisterType.phone, RegisterType.email];
const NEED_PHONE_VALIDATE = getUrlParameter(URL_PARAMETER.NEED_VALIDATE) === 'true';
// 手机号 + 验证码注册报错超一定次数后的默认锁定时间（分钟）
const DEFAULT_PHONE_REGISTER_LOCK_TIME = 60;

const RegisterForm: React.FC<RegisterFormProps> = (props) => {
    const {
        items = DEFAULT_REGISTER_ITEMS,
        skipAppInstall,
        suiteId,
        onRegister,
        onComplete
    } = props;

    const [registerType, setRegisterType] = useState(() => {
        if (defaultPhone) {
            return RegisterType.phone;
        }
        if (defaultEmail) {
            return RegisterType.email;
        }
        return items[0];
    });
    const [defaultValue] = useState<AccountFormValue>(() => {
        let phone = defaultPhone;
        if (phone && NEED_PHONE_VALIDATE) {
            phone = phone.replace(/[^\d]/g, '');
            phone = phone ? getChinaCode() + '-' + phone : '';
        }
        return {
            phone,
            email: defaultEmail,
            nickname: defaultNickname
        };
    });
    const [errorText, setErrorText] = useState<string>();
    const [hasRegistered, setHasRegistered] = useState<boolean>(false);

    const { renderAuthMfa, joinCorp } = useJoinCorp();

    const accountFormRef = useRef<AccountFormRef>(null);

    const isPhoneMode = registerType === RegisterType.phone;

    const accountFormItems = useMemo(() => {
        const hasNickname = isPublicInvitation;
        if (isPhoneMode) {
            const phoneDisabled = !!defaultPhone && !NEED_PHONE_VALIDATE;
            return compact([
                {
                    type: AccountFieldType.phone,
                    disabled: phoneDisabled,
                    autoSelect: !phoneDisabled
                },
                { type: AccountFieldType.phone_code },
                hasNickname && { type: AccountFieldType.nickname }
            ]);
        }
        const emailDisabled = !!defaultEmail;
        return compact([
            {
                type: AccountFieldType.email,
                disabled: emailDisabled,
                autoSelect: !emailDisabled
            },
            { type: AccountFieldType.email_code },
            hasNickname && { type: AccountFieldType.nickname },
            { type: AccountFieldType.password }
        ]);
    }, [registerType]);

    const handleJoinCorp = () => {
        const { invitedBy, notJoinedMemberId } = window.jdy_invitation_info || {};
        return joinCorp([
            {
                corpId: get(invitedBy, 'corp_id', ''),
                memberId: notJoinedMemberId || ''
            }
        ]);
    };

    const handleRegister = () => {
        /**
         * 在邀请注册的某些场景下，就算已经注册成功，但还可能停留在注册页面（比如触发MFA）
         * 此时再次点击注册，则直接调用加入企业接口
         */
        if (isInvitationMode && hasRegistered) {
            return handleJoinCorp();
        }

        const formValue = accountFormRef.current?.getValue();
        if (isEmpty(formValue)) {
            return;
        }
        const commonParams = {
            nickname: formValue?.nickname
        };
        switch (registerType) {
            case RegisterType.phone: {
                if (isInvitationMode) {
                    return doRegister({
                        phone: formValue?.phone,
                        phone_code: formValue?.phone_code,
                        ...commonParams
                    });
                }
                return doRegister(
                    {
                        username: formValue?.phone,
                        phone_code: formValue?.phone_code,
                        suite_id: suiteId ?? window.platform_suite_id,
                        ...commonParams
                    },
                    getApi(API.authentication.signin)
                );
            }
            case RegisterType.email: {
                return doRegister({
                    email: formValue?.email,
                    email_code: formValue?.email_code,
                    password: getEncryptedPassword(formValue?.password || ''),
                    ...commonParams
                });
            }
        }
    };

    const doRegister = (
        data: {
            phone?: string;
            phone_code?: string;
            email?: string;
            email_code?: string;
            password?: string;
            username?: string;
            nickname?: string;
            suite_id?: number;
        },
        url?: string
    ) => {
        if (skipAppInstall) {
            assign(data, { is_skip_install: true });
        }
        const memberType = getUrlParameter(URL_PARAMETER.MEMBER_TYPE);
        if (isInvitationMode && memberType) {
            assign(data, { member_type: Number(memberType) });
        }
        let apiUrl = getApi(API.authentication.register);
        let isRegister = true;
        if (url) {
            // 调用 signin 接口
            apiUrl = url;
            isRegister = false;
        } else {
            assign(data, getAuthParamsOnRegister());
        }
        return post(apiUrl, data, { customHandleHttpCodes: [400] })
            .then((res) => {
                const isSignInRegister = get(res, 'data.is_register', false);
                setHasRegistered(true);
                if (isSignInRegister || isRegister) {
                    // 触发百度OCPC回传
                    window._agl && window._agl.push(['track', ['success', { t: 3 }]]);
                    if (!isInvitationMode) {
                        // 上报神策登录ID，邀请注册场景不用关注
                        window.sensors?.login(res?.data?.user?._id);
                    }
                    if (onRegister) {
                        return onRegister(get(res, 'data', {}));
                    }
                    if (isInvitationMode) {
                        return handleJoinCorp();
                    } else {
                        const user = get(res, 'data.user');
                        if (user) {
                            /**
                             * 注册场景下，在创建企业前是没有企业信息的，所以corp_id和tenant_id为空
                             * @see https://fanruan-x.feishu.cn/wiki/FImgwWPBgiJaJhkfM27cvs6gnaf 第21-2条
                             */
                            setTrackerConfig({
                                username: user.username,
                                corp_id: '',
                                tenant_id: '',
                                user_id: user._id,
                                member_id: ''
                            });
                            tracker(TRACKER.SELF_REG_SUCCESS);
                        }
                        onComplete?.({
                            invitations: get(res, 'data.invitations', [])
                        });
                    }
                } else {
                    handleRedirect(suiteId);
                }
            })
            .catch((error) => {
                const resData = get(error, 'response.data');
                switch (resData.code) {
                    case ECode.USER_EMAIL_HAS_BEEN_REGISTERED: {
                        accountFormRef.current?.showError(AccountFieldType.email);
                        setErrorText(Intl.get('account.register.email.exist'));
                        break;
                    }
                    case ECode.USER_LOGIN_FAILED_EXCEEDED: {
                        /**
                         * 如果注册时使用手机号 + 验证码多次因验证码失败，按照之前的平台安全设计，此时需要锁定该账户
                         * @see https://project.feishu.cn/b2rl2h/assignment/detail/6243415390
                         */
                        // 是否是因为验证码而失败
                        const isVerifyCodeLocked = resData?.meta?.lock_type === 'phone';
                        if (isVerifyCodeLocked) {
                            // 该账号的锁定时长
                            const lockingTime =
                                resData?.meta?.locking_time ?? DEFAULT_PHONE_REGISTER_LOCK_TIME;
                            setErrorText(
                                Intl.get('fxp.base.workspace.login.captcha_count_limit.tip', {
                                    time: lockingTime
                                })
                            );
                        } else {
                            // 如果不是因验证码失败，则按原先的默认逻辑来处理
                            setErrorText(getErrorMsg(resData));
                        }
                        break;
                    }
                    case ECode.USER_PHONE_AUTH_CODE_INVALID:
                    case ECode.USER_EMAIL_AUTH_CODE_INVALID: {
                        accountFormRef.current?.showError(
                            isPhoneMode ? AccountFieldType.phone_code : AccountFieldType.email_code
                        );
                        setErrorText(Intl.get('account.register.code.err'));
                        break;
                    }
                    case ECode.USER_EMAIL_IS_INCORRECT: {
                        accountFormRef.current?.showError(AccountFieldType.email);
                        setErrorText(Intl.get('email.invalid.warning'));
                        break;
                    }
                    case ECode.USER_EMAIL_DOMAIN_BLOCKED: {
                        accountFormRef.current?.showError(AccountFieldType.email);
                        setErrorText(getErrorMsg(resData));
                        break;
                    }
                    default: {
                        setErrorText(getErrorMsg(resData));
                        break;
                    }
                }
            });
    };

    const showSwitchText = () => {
        return isAccountInvitation ? false : size(items) > 1;
    };

    const { termServiceLink, termPrivacyLink } = getBrandInfo();

    return (
        <div
            className={classNames('fx-register-form', {
                'is-mobile': isMobileUA()
            })}
        >
            {errorText && <div className="error-tip">{errorText}</div>}
            <AccountForm
                defaultValue={defaultValue}
                ref={accountFormRef}
                items={accountFormItems}
                showErrorMsg={!errorText}
                onError={setErrorText}
                onChange={() => {
                    setErrorText('');
                }}
            />
            <div className="service-laws">
                {termServiceLink &&
                    termPrivacyLink &&
                    Intl.getHTML('account.register.read.accept', {
                        TermLink: (s) => (
                            <Hyperlink compact underline={false} href={termServiceLink}>
                                {s}
                            </Hyperlink>
                        ),
                        PrivacyLink: (s) => (
                            <Hyperlink compact underline={false} href={termPrivacyLink}>
                                {s}
                            </Hyperlink>
                        )
                    })}
            </div>
            <Button
                className="register-btn"
                type="primary"
                size="large"
                block
                loadingWhenClick
                onClick={handleRegister}
            >
                {isInvitationMode ? Intl.get('signup.join') : Intl.get('signup')}
            </Button>
            {showSwitchText() && (
                <div className="switch-type">
                    <span>
                        {isPhoneMode
                            ? Intl.get('account.register.switch.phone')
                            : Intl.get('account.register.switch.email')}
                    </span>
                    <span
                        className="switch-btn"
                        onClick={() => {
                            setErrorText('');
                            setRegisterType(isPhoneMode ? RegisterType.email : RegisterType.phone);
                        }}
                    >
                        {Intl.get('account.register.switch')}
                    </span>
                </div>
            )}
            {isInvitationMode && renderAuthMfa()}
        </div>
    );
};

export default RegisterForm;
